import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { 
  ArrowLeft, 
  Search, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  MessageSquare,
  User,
  Calendar,
  Filter,
  BookOpen,
  Loader
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContributionManagement = ({ onBack }) => {
  const { user, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [contributions, setContributions] = useState([]);
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchContributions();
    fetchStats();
  }, [currentPage, statusFilter]);

  const fetchContributions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });
      
      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await axios.get(`${API}/admin/contributions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setContributions(response.data.contributions || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (error) {
      toast.error('Error al cargar las contribuciones');
      console.error('Error fetching contributions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/contributions/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching contribution stats:', error);
    }
  };

  const handleApproveContribution = async (contributionId) => {
    console.log('handleApproveContribution called with ID:', contributionId);
    
    if (!window.confirm('¿Estás seguro de que deseas aprobar esta contribución?')) {
      return;
    }

    try {
      setIsProcessing(true);
      console.log('Making approve request to:', `${API}/admin/contributions/${contributionId}/approve`);
      
      const response = await axios.put(`${API}/admin/contributions/${contributionId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Approve response:', response.data);
      toast.success('Contribución aprobada exitosamente');
      await fetchContributions();
      await fetchStats();
      setShowViewModal(false);
    } catch (error) {
      console.error('Error approving contribution:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`Error al aprobar la contribución: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectContribution = async (contributionId, notes = '') => {
    console.log('handleRejectContribution called with ID:', contributionId, 'notes:', notes);
    
    if (!window.confirm('¿Estás seguro de que deseas rechazar esta contribución?')) {
      return;
    }

    try {
      setIsProcessing(true);
      console.log('Making reject request to:', `${API}/admin/contributions/${contributionId}/reject`);
      
      const response = await axios.put(`${API}/admin/contributions/${contributionId}/reject`, 
        { admin_notes: notes }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Reject response:', response.data);
      toast.success('Contribución rechazada');
      await fetchContributions();
      await fetchStats();
      setShowViewModal(false);
    } catch (error) {
      console.error('Error rejecting contribution:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`Error al rechazar la contribución: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const openViewModal = (contribution) => {
    setSelectedContribution(contribution);
    setShowViewModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pendiente
          </span>
        );
      case 'approved':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Aprobada
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Rechazada
          </span>
        );
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 bg-white hover:bg-gray-50 px-4 py-2 rounded-xl shadow-md transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              Volver
            </button>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">
                Gestión de Contribuciones
              </h1>
              <p className="text-emerald-600">
                Administra las contribuciones de los usuarios
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => fetchStats()}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl shadow-md transition-all duration-300"
            >
              <MessageSquare className="w-5 h-5" />
              Actualizar
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total</p>
                <p className="text-2xl font-bold text-emerald-800">{stats.total_contributions || 0}</p>
              </div>
              <BookOpen className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Pendientes</p>
                <p className="text-2xl font-bold text-yellow-800">{stats.pending_contributions || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Aprobadas</p>
                <p className="text-2xl font-bold text-green-800">{stats.approved_contributions || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Rechazadas</p>
                <p className="text-2xl font-bold text-red-800">{stats.rejected_contributions || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex flex-col lg:flex-row gap-4">            
            <div className="flex gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Todos los estados</option>
                <option value="pending">Pendientes</option>
                <option value="approved">Aprobadas</option>
                <option value="rejected">Rechazadas</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contributions Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-emerald-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Usuario</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Oddun</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Sección</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Tipo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Estado</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Fecha</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Loader className="w-8 h-8 animate-spin mx-auto text-emerald-500" />
                      <p className="text-gray-600 mt-2">Cargando contribuciones...</p>
                    </td>
                  </tr>
                ) : contributions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <MessageSquare className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">No se encontraron contribuciones</p>
                    </td>
                  </tr>
                ) : (
                  contributions.map((contribution) => (
                    <tr key={contribution.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          {contribution.user_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        Oddun #{contribution.oddun_id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{contribution.section}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 capitalize">{contribution.type}</td>
                      <td className="px-6 py-4 text-sm">
                        {getStatusBadge(contribution.status)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(contribution.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openViewModal(contribution)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {contribution.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApproveContribution(contribution.id)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Aprobar"
                                disabled={isProcessing}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRejectContribution(contribution.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Rechazar"
                                disabled={isProcessing}
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Página {currentPage} de {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* View Modal */}
        {showViewModal && selectedContribution && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-cinzel text-2xl font-bold text-emerald-800">
                    Detalles de Contribución
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Usuario</h4>
                    <p className="text-gray-600">{selectedContribution.user_name}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Estado</h4>
                    {getStatusBadge(selectedContribution.status)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Oddun</h4>
                    <p className="text-gray-600">Oddun #{selectedContribution.oddun_id}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Sección</h4>
                    <p className="text-gray-600">{selectedContribution.section}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Tipo</h4>
                    <p className="text-gray-600 capitalize">{selectedContribution.type}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Fecha</h4>
                    <p className="text-gray-600">
                      {new Date(selectedContribution.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Contenido de la Contribución</h4>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedContribution.content}</p>
                  </div>
                </div>

                {selectedContribution.admin_notes && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Notas del Admin</h4>
                    <div className="bg-blue-50 rounded-xl p-4">
                      <p className="text-blue-700">{selectedContribution.admin_notes}</p>
                    </div>
                  </div>
                )}

                {selectedContribution.status === 'pending' && (
                  <div className="flex gap-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleApproveContribution(selectedContribution.id)}
                      disabled={isProcessing}
                      className="flex-1 bg-green-500 text-white py-3 rounded-xl font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isProcessing ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          Aprobar
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleRejectContribution(selectedContribution.id)}
                      disabled={isProcessing}
                      className="flex-1 bg-red-500 text-white py-3 rounded-xl font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isProcessing ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5" />
                          Rechazar
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContributionManagement;